// Drives the "1 Set" / "Buy 2, Get 1 FREE" tier picker (bundle-tier-picker.liquid).
//
// The running total across the variant rows' quantity steppers is the one
// source of truth for everything else on screen: the progress bar/dots, the
// guide message, the add-to-cart button's text/price/disabled state, and
// which tier card (if any) looks selected are all *derived* from it in
// updateBundleTotal, not tracked independently. Clicking a tier card is just
// a shortcut that sets the rows to match it (applyTierQuantity) and lets
// that same sync logic take over — so switching tiers, stepping quantities
// up/down, or emptying everything back to 0 all stay consistent.
//
// The shopper can stack up to MAX_QUANTITY pairs, not just one bundle's
// worth — every full group of BUNDLE_TOTAL (3) pairs earns another free one
// via the existing "Osta 2, saa 1 tasuta" automatic Buy X Get Y discount
// (Shopify Admin → Discounts), which is what actually makes it free at
// cart/checkout. The progress bar/dots/message only track the shopper's way
// to that *first* free pair (0–3) and then just sit at "done" — they don't
// cycle for every extra group past that, which read as the bar resetting
// and re-animating itself once you were ordering in bulk. Past 3, only the
// add-to-cart button's price keeps moving, computed from the same per-group
// math (paid = total minus one free pair per completed group) and formatted
// client-side with the theme's own @theme/money-formatting helper — this
// file only echoes the shop's real unit price (in the add-to-cart button's
// data attributes), it never invents a price. Items are added in one
// request via ProductFormComponent#addBatchToCart (the public wrapper added
// in product-form.js around its private batch-add method), so price/media/
// url machinery is untouched.
//
// Bumping a row's quantity up (or landing on it via a tier click) also
// jumps the gallery to that variant's own photo (syncGalleryImage), by
// matching the row's data-media-id against the gallery's own slides —
// this relies on the media-gallery block's hide_variants setting being off
// (templates/product.json), so every variant's photo is actually present
// as a slide rather than filtered out. The jump is instant (not animated),
// since select()'s animated path does a whole placeholder/reorder dance for
// non-adjacent slides (see slideshow.js) that reads as sluggish for what
// should feel like an immediate photo swap. This sidesteps the full variant
// section-refetch pipeline a native swatch picker uses, since this picker
// never "selects" a Shopify variant in the form sense.
import { formatMoney } from '@theme/money-formatting';
import { SlideshowSelectEvent } from '@theme/events';

const BUNDLE_TOTAL = 3;
const MAX_QUANTITY = 100;

// Silicone product only ("Osta 3, saa 2 tasuta"): the picker is rendered with
// data-bundle-five="true". 5 pairs cost 3. Two automatic Buy X Get Y discounts exist in
// Shopify ("Osta 2, saa 1 tasuta" and "Osta 3, saa 2 tasuta"); they can't combine, so
// checkout applies whichever gives the shopper more free pairs. freeCount mirrors that.
const FIVE_PACK_SIZE = 5;

/**
 * @param {Element | null} picker
 * @returns {boolean}
 */
function hasFivePack(picker) {
  return picker instanceof HTMLElement && picker.dataset.bundleFive === 'true';
}

/**
 * Free pairs for a total, as the store's automatic discounts give them.
 * @param {Element | null} picker
 * @param {number} total
 * @returns {number}
 */
function freeCount(picker, total) {
  const perThree = Math.floor(total / BUNDLE_TOTAL);
  const perFive = hasFivePack(picker) ? 2 * Math.floor(total / FIVE_PACK_SIZE) : 0;
  return Math.max(perThree, perFive);
}

// A soft flash on the gallery's main image every time the slide changes —
// not just from this picker's own syncGalleryImage, but from the gallery's
// native arrows/drag/thumbnails too, since `slideshow:select` bubbles from
// every one of those paths (see slideshow.js) and this listens document-wide.
document.addEventListener(SlideshowSelectEvent.eventName, (event) => {
  const image = /** @type {any} */ (event).detail?.slide?.querySelector('.product-media__image');
  if (!(image instanceof HTMLElement)) return;

  image.classList.remove('bundle-gallery-flash');
  void image.offsetWidth; // restart the animation if it's already mid-flash
  image.classList.add('bundle-gallery-flash');
});

/** @type {Record<number, string>} */
const PROGRESS_MESSAGES = {
  1: 'Lisa 1 veel ja saa 1 Paar tasuta',
  2: 'Vali tasuta paar',
  3: 'Vahva! Said 1 Paari tasuta',
};

/** @type {Record<number, string>} */
const ADD_TO_CART_TEXT = {
  1: 'Lisa Ostukorvi',
  2: 'Lisa 1 Paar veel',
  3: 'Lisa Ostukorvi',
  4: 'Lisa 1 Paar veel',
  5: 'Lisa Ostukorvi',
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const tierButton = target.closest('[data-bundle-tier]');
  if (tierButton) {
    handleTierClick(tierButton);
    return;
  }

  const qtyButton = target.closest('[data-bundle-qty-minus], [data-bundle-qty-plus]');
  if (qtyButton instanceof HTMLButtonElement) {
    handleQtyClick(qtyButton);
    return;
  }

  const addButton = target.closest('[data-bundle-add-to-cart]');
  if (addButton instanceof HTMLButtonElement) {
    handleBundleAddToCart(addButton);
    return;
  }

  const validationDialog = target.closest('[data-bundle-validation-dialog]');
  if (validationDialog instanceof HTMLDialogElement) {
    // Closes on the "Selge" button, and on a backdrop click too — a click
    // on the backdrop lands on the <dialog> element itself, not any of its
    // (unclicked) content, which is what the `target === validationDialog`
    // check below is for.
    if (target.closest('[data-bundle-validation-dialog-close]') || target === validationDialog) {
      validationDialog.close();
    }
  }
});

/**
 * How many pairs of this variant can be bought at most: its stock when the store stops selling at 0
 * (data-max-quantity, set in bundle-variant-row.liquid), otherwise no limit.
 * @param {Element} row
 * @returns {number}
 */
function getRowMax(row) {
  const max = parseInt(/** @type {HTMLElement} */ (row).dataset.maxQuantity ?? '', 10);
  return Number.isNaN(max) ? Infinity : max;
}

/** @type {number} */
let stockNoticeTimer = 0;

/**
 * Shows a short notice under the picker (the same line the "not available" error uses).
 * @param {Element} picker
 * @param {string} text
 */
function showStockNotice(picker, text) {
  const errorEl = picker.querySelector('[data-bundle-error]');
  if (!(errorEl instanceof HTMLElement)) return;

  errorEl.textContent = text;
  errorEl.hidden = false;
  clearTimeout(stockNoticeTimer);
  stockNoticeTimer = window.setTimeout(() => {
    errorEl.hidden = true;
  }, 4000);
}

/** @param {Element} tierButton */
function handleTierClick(tierButton) {
  const picker = tierButton.closest('[data-bundle-tier-picker]');
  if (!(picker instanceof HTMLElement)) return;

  const tier = tierButton.getAttribute('data-bundle-tier');
  const targetTotal = tier === '5' ? FIVE_PACK_SIZE : tier === '3' ? 3 : 1;
  applyTierQuantity(picker, targetTotal);
}

/**
 * Picking a tier ("1 Paar" or "Osta 2, saa 1 TASUTA") is a one-click
 * shortcut that sets the variant rows to match it exactly — one variant at
 * the target quantity, every other row zeroed — so the progress bar, tier
 * selection highlight, and add-to-cart button all jump straight to that
 * state instead of waiting on stepper clicks. Whichever row already has a
 * quantity (the shopper's own pick) is kept as the target; otherwise the
 * first available row is used.
 * @param {Element} picker
 * @param {number} targetTotal
 */
function applyTierQuantity(picker, targetTotal) {
  const rows = Array.from(picker.querySelectorAll('[data-bundle-variant-row]')).filter(
    (row) => row instanceof HTMLElement && row.dataset.available !== 'false'
  );
  if (rows.length === 0) return;

  const target =
    rows.find((row) => {
      const valueEl = row.querySelector('[data-bundle-qty-value]');
      return parseInt(valueEl?.textContent || '0', 10) > 0;
    }) ?? rows[0];

  // The chosen variant first, up to what is in stock; whatever does not fit spills over to the
  // other variants in order (e.g. 5 pairs with only 3 black in stock: 3 black + 2 of the next).
  const ordered = [target, ...rows.filter((row) => row !== target)];
  let left = targetTotal;
  for (const row of ordered) {
    const valueEl = row.querySelector('[data-bundle-qty-value]');
    if (!(valueEl instanceof HTMLElement)) continue;
    const qty = Math.min(left, getRowMax(row));
    valueEl.textContent = String(qty);
    left -= qty;
  }

  updateBundleTotal(picker);
  if (target instanceof HTMLElement) scheduleGallerySync(target);
}

/** @param {HTMLButtonElement} button */
function handleQtyClick(button) {
  const row = button.closest('[data-bundle-variant-row]');
  if (!(row instanceof HTMLElement) || row.dataset.available === 'false') return;

  const valueEl = row.querySelector('[data-bundle-qty-value]');
  if (!(valueEl instanceof HTMLElement)) return;

  const picker = row.closest('[data-bundle-tier-picker]');
  let qty = parseInt(valueEl.textContent || '0', 10) || 0;
  const isPlus = button.hasAttribute('data-bundle-qty-plus');

  if (isPlus) {
    if (getTotalQuantity(picker) >= MAX_QUANTITY) return;
    if (qty >= getRowMax(row)) {
      if (picker instanceof HTMLElement) showStockNotice(picker, `Sellest värvist on laos ainult ${qty} tk.`);
      return;
    }
    qty += 1;
  } else {
    if (qty <= 0) return;
    qty -= 1;
  }

  valueEl.textContent = String(qty);
  // The number, bar, price and button are what the shopper is waiting for: update them first so
  // the browser can paint right after the tap. The gallery photo swap is the heavy part
  // (slideshow selection, events, flash animation) and follows once the screen has updated.
  updateBundleTotal(picker);
  scheduleGallerySync(row);
}

/** @type {number} */
let gallerySyncTimer = 0;

/**
 * Swaps the gallery photo after the UI has painted. Quick repeated taps only swap once, to the
 * row that was tapped last, instead of queueing a swap for every tap.
 * @param {HTMLElement} row
 */
function scheduleGallerySync(row) {
  clearTimeout(gallerySyncTimer);
  gallerySyncTimer = window.setTimeout(() => {
    requestAnimationFrame(() => syncGalleryImage(row));
  }, 140);
}

/**
 * Swaps the main product gallery to the row's variant photo, same as
 * picking a color swatch would — smooth animated slide, not an instant cut.
 * @param {HTMLElement} row
 */
function syncGalleryImage(row) {
  const mediaId = row.dataset.mediaId;
  if (!mediaId) return;

  const gallery = row.closest('.shopify-section')?.querySelector('media-gallery');
  const slideshow = /** @type {any} */ (gallery)?.slideshow;
  const slides = slideshow?.refs?.slides;
  if (!Array.isArray(slides)) return;

  const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
  // Instant, not animated: select()'s default animated jump does a whole
  // placeholder/reorder dance for any non-adjacent slide (see slideshow.js),
  // which reads as sluggish for what should be an immediate photo swap.
  if (index > -1) slideshow.select(index, undefined, { animate: false });
}

/** @param {Element | null} picker */
function getTotalQuantity(picker) {
  if (!(picker instanceof Element)) return 0;

  let total = 0;
  for (const valueEl of picker.querySelectorAll('[data-bundle-qty-value]')) {
    total += parseInt(valueEl.textContent || '0', 10) || 0;
  }
  return total;
}

/** @param {Element | null} picker */
function updateBundleTotal(picker) {
  if (!(picker instanceof Element)) return;

  const total = getTotalQuantity(picker);
  // Capped, not cyclical — the bar/dots/message only track the way to the
  // *first* free pair, then stay at "done" no matter how much more is added.
  // With the 5-pack on offer the bar runs 1 to 5 pairs (one dot per pair) instead of 1 to 3.
  const five = hasFivePack(picker);
  const maxStep = five ? FIVE_PACK_SIZE : BUNDLE_TOTAL;
  const progressStep = Math.min(total, maxStep);
  // 2 pairs (and, with the 5-pack, 4 pairs) is one short of a bundle: the arrow dot and the
  // disabled "Lisa 1 Paar veel" button nudge the shopper to add one more.
  const nudgeStep = total === 2 ? 2 : five && total === 4 ? 4 : 0;

  // The bar itself only starts moving once past the first pair — at 1 it
  // sits at 0% (everything else already goes "active": the dot fills in,
  // the message shows), reaching exactly the middle at 2 and full at 3.
  const fillEl = picker.querySelector('[data-bundle-progress-fill]');
  if (fillEl instanceof HTMLElement) {
    const fillPercent = progressStep <= 1 ? 0 : ((progressStep - 1) / (maxStep - 1)) * 100;
    fillEl.style.width = `${fillPercent}%`;
  }

  for (const dot of picker.querySelectorAll('[data-bundle-progress-dot]')) {
    const step = parseInt(dot.getAttribute('data-bundle-progress-dot') || '0', 10);
    const reached = progressStep >= step;
    dot.classList.toggle('bundle-progress__dot--reached', reached);
    // The middle dot points down at the free-pair picker once it's reached,
    // then settles into the normal checkmark once the run is complete.
    dot.classList.toggle('bundle-progress__dot--arrow', nudgeStep !== 0 && step === nudgeStep);
  }

  const cueEl = picker.querySelector('[data-bundle-progress-cue]');
  if (cueEl instanceof HTMLElement) {
    // The down-arrow cue under the bar is gone on the 5-pack bar (the shimmering message and the
    // disabled button do the nudging); the 3-step bar of the chrome product keeps it.
    cueEl.hidden = five || nudgeStep === 0;
    // The cue sits under the arrow dot: dot 2 is at 50% (3-step bar) or 25% (5-step bar), dot 4 at 75%.
    if (nudgeStep === 2) cueEl.style.left = five ? '25%' : '50%';
    if (nudgeStep === 4) cueEl.style.left = '75%';
  }

  for (const label of picker.querySelectorAll('[data-bundle-progress-label]')) {
    const step = parseInt(label.getAttribute('data-bundle-progress-label') || '0', 10);
    label.classList.toggle('bundle-progress__label--reached', progressStep >= step);
  }

  const progressEl = picker.querySelector('[data-bundle-total]');
  if (progressEl instanceof HTMLElement) {
    progressEl.classList.toggle('bundle-progress--empty', total === 0);
  }

  const messageEl = picker.querySelector('[data-bundle-progress-message]');
  const messageTextEl = picker.querySelector('[data-bundle-progress-message-text]');
  // With the 5-pack on offer, the message goes on past the first free pair: at 4 pairs one
  // more earns a second free pair, from 5 it states how many pairs are free.
  let messageText = PROGRESS_MESSAGES[progressStep] ?? '';
  if (hasFivePack(picker) && total === 4) {
    messageText = 'Lisa 1 veel ja saa 2 Paari tasuta';
  } else if (hasFivePack(picker) && total === FIVE_PACK_SIZE) {
    messageText = `Vahva! Said ${freeCount(picker, total)} Paari tasuta + tasuta Üllatuskingi`;
  } else if (hasFivePack(picker) && total > FIVE_PACK_SIZE) {
    // Past the 5-pack nothing more can be unlocked, however many pairs are added (6, 20, ...).
    // A shorter sentence on phones, where the message box is narrow.
    messageText = window.matchMedia('(max-width: 749px)').matches
      ? 'Said meie parima pakkumise! Võid kõrvatroppe juurde lisada.'
      : 'Said meie parima pakkumise! Võid iga hetk kõrvatroppe juurde lisada.';
  }
  const showMessage = total !== 0;
  if (messageEl instanceof HTMLElement) {
    messageEl.hidden = !showMessage;
  }
  if (messageTextEl instanceof HTMLElement) {
    messageTextEl.textContent = showMessage ? messageText : '';
  }
  // Every message gets the soft light sweep that repeats every few seconds.
  if (messageEl instanceof HTMLElement) {
    messageEl.classList.toggle('bundle-progress__message--shimmer', showMessage);
  }

  const isNudge = nudgeStep !== 0;
  const addButton = picker.querySelector('[data-bundle-add-to-cart]');
  if (addButton instanceof HTMLButtonElement) {
    // 0 selected stays clickable — it shows the real 0 € price like any
    // other total, and clicking it just surfaces the validation dialog
    // instead of silently doing nothing.
    addButton.disabled = isNudge;
    addButton.classList.toggle('bundle-add-to-cart--nudge', isNudge);
  }

  updateAddToCartText(picker, progressStep);
  updateAddToCartPrice(picker, total);
  syncTierSelection(picker, total);
}

/**
 * The tier cards' "selected" highlight is a pure reflection of the running
 * total, not independent state — 1 pair matches "1 Paar"; 2 or more pairs
 * already read as committed to the bundle (2 is one tap from completing it,
 * 3+ is the bundle plus extra groups), so all match "Osta 2, saa 1 TASUTA";
 * 0 matches neither. Keeps the
 * cards in sync no matter how the total changed: tier click, steppers, or a
 * completed add-to-cart resetting everything back to 0.
 * @param {Element} picker
 * @param {number} total
 */
function syncTierSelection(picker, total) {
  for (const btn of picker.querySelectorAll('[data-bundle-tier]')) {
    const tier = btn.getAttribute('data-bundle-tier');
    const five = hasFivePack(picker);
    let selected;
    if (tier === '5') selected = total >= FIVE_PACK_SIZE;
    else if (tier === '3') selected = total >= 2 && !(five && total >= FIVE_PACK_SIZE);
    else selected = total === 1;
    btn.classList.toggle('bundle-tier--selected', selected);
    btn.setAttribute('aria-pressed', String(selected));
  }
}

/**
 * @param {Element} picker
 * @param {number} total
 */
function updateAddToCartText(picker, total) {
  const textEl = picker.querySelector('[data-bundle-add-to-cart-text]');
  if (!(textEl instanceof HTMLElement)) return;

  textEl.textContent = ADD_TO_CART_TEXT[total] ?? ADD_TO_CART_TEXT[1];
}

/**
 * Every full group of BUNDLE_TOTAL (3) pairs is 2 paid + 1 free, same math
 * as the store's automatic "Osta 2, saa 1 tasuta" discount applies at
 * checkout — so N total pairs cost `(N - floor(N / 3))` units, whatever N
 * is. Formatted with the theme's own money helper using the real unit
 * price and the shop's money format, both read off the button's own data
 * attributes (no price is invented here).
 * @param {Element} picker
 * @param {number} total
 */
function updateAddToCartPrice(picker, total) {
  const addButton = picker.querySelector('[data-bundle-add-to-cart]');
  const nowEl = picker.querySelector('[data-bundle-savings-now]');
  const compareEl = picker.querySelector('[data-bundle-savings-compare]');
  if (!(addButton instanceof HTMLElement) || !(nowEl instanceof HTMLElement) || !(compareEl instanceof HTMLElement)) {
    return;
  }

  const unitPrice = parseInt(addButton.dataset.unitPrice || '0', 10) || 0;
  const moneyFormat = addButton.dataset.moneyFormat || '{{amount}}';
  const currency = addButton.dataset.currency || 'USD';

  const free = freeCount(picker, total);
  const paidCount = total - free;

  nowEl.textContent = formatMoney(paidCount * unitPrice, moneyFormat, currency);
  if (free > 0) {
    compareEl.textContent = formatMoney(total * unitPrice, moneyFormat, currency);
    compareEl.hidden = false;
  } else {
    compareEl.hidden = true;
  }
}

/** @param {HTMLButtonElement} addButton */
async function handleBundleAddToCart(addButton) {
  const picker = addButton.closest('[data-bundle-tier-picker]');
  const errorEl = picker?.querySelector('[data-bundle-error]');
  if (!(picker instanceof HTMLElement)) return;

  const total = getTotalQuantity(picker);
  // One short of a bundle (2 pairs, or 4 with the 5-pack): the button is disabled, so nothing to add.
  if (total === 2 || (hasFivePack(picker) && total === 4)) return;

  if (total < 1) {
    const dialog = document.querySelector('[data-bundle-validation-dialog]');
    if (dialog instanceof HTMLDialogElement) dialog.showModal();
    return;
  }

  /** @type {{variantId: string, quantity: number}[]} */
  const items = [];

  for (const row of picker.querySelectorAll('[data-bundle-variant-row]')) {
    const valueEl = row.querySelector('[data-bundle-qty-value]');
    const qty = parseInt(valueEl?.textContent || '0', 10) || 0;
    if (qty <= 0) continue;

    if (row.dataset.available === 'false') {
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = 'Üks valitud toodetest pole hetkel saadaval — vali mõni teine.';
        errorEl.hidden = false;
      }
      return;
    }

    if (qty > getRowMax(row)) {
      if (errorEl instanceof HTMLElement) {
        errorEl.textContent = `Sellest värvist on laos ainult ${getRowMax(row)} tk. Vähenda kogust või vali mõni teine värv.`;
        errorEl.hidden = false;
      }
      return;
    }

    items.push({ variantId: String(row.dataset.variantId), quantity: qty });
  }

  if (errorEl instanceof HTMLElement) errorEl.hidden = true;

  const productForm = document.querySelector('product-form-component');
  if (!productForm || typeof (/** @type {any} */ (productForm).addBatchToCart) !== 'function') {
    console.error('[bundle-tier-picker] product-form-component with addBatchToCart not found');
    return;
  }

  addButton.disabled = true;
  try {
    /** @type {any} */ (productForm).addBatchToCart(items);
  } finally {
    setTimeout(() => {
      for (const valueEl of picker.querySelectorAll('[data-bundle-qty-value]')) {
        valueEl.textContent = '0';
      }
      updateBundleTotal(picker);
    }, 600);
  }
}
